import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step", "stepIndicator", "stepLine",
    "card", "errors", "submitBtn",
    "addressRadio", "billingNextBtn",
    "recapAddress", "recapPayment",
    "methodCard",
    "stripeSection", "paypalSection",
    "paypalButtonContainer", "paypalConfirmButtonContainer",
    "stripeSubmitSection", "paypalSubmitSection",
    "bitcoinSection", "bitcoinSubmitSection", "bitcoinSubmitBtn",
    "confirmErrors"
  ]
  static values = {
    publishableKey:   String,
    eventId:          Number,
    checkoutUrl:      String,
    paypalCreateUrl:  String,
    paypalCaptureUrl: String,
    bitcoinCreateUrl: String,
    returnUrl:        String
  }

  connect() {
    this.currentStep = 0
    this.stripeReady = false
    this.paypalReady = false
    this.selectedMethod = "stripe"
    this.updateStepUI()
  }

  // ——— Step navigation ———

  nextStep() {
    if (this.currentStep >= 3) return

    // Validation per step
    if (this.currentStep === 1 && !this.validateBilling()) return

    this.currentStep++
    this.updateStepUI()

    // Lazy-init Stripe al primo accesso allo step 2
    if (this.currentStep === 2 && !this.stripeReady) {
      this.initStripe()
    }

    // Aggiorna recap allo step 3
    if (this.currentStep === 3) {
      this.updateRecap()
      this.updateConfirmUI()
    }

    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  prevStep() {
    if (this.currentStep <= 0) return
    this.currentStep--
    this.updateStepUI()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  updateStepUI() {
    // Show/hide step panels
    this.stepTargets.forEach((el) => {
      const step = parseInt(el.dataset.step)
      el.classList.toggle("d-none", step !== this.currentStep)
    })

    // Update stepper indicators
    this.stepIndicatorTargets.forEach((el) => {
      const step = parseInt(el.dataset.step)
      el.classList.remove("checkout-stepper__step--active", "checkout-stepper__step--done")
      if (step < this.currentStep) {
        el.classList.add("checkout-stepper__step--done")
      } else if (step === this.currentStep) {
        el.classList.add("checkout-stepper__step--active")
      }
    })

    // Update connector lines
    this.stepLineTargets.forEach((el, i) => {
      el.classList.toggle("checkout-stepper__line--done", i < this.currentStep)
    })
  }

  // ——— Validation ———

  validateBilling() {
    const selected = this.addressRadioTargets.find(r => r.checked)
    if (!selected) {
      alert("Seleziona un indirizzo di fatturazione.")
      return false
    }
    return true
  }

  // ——— Payment method toggle ———

  selectMethod(event) {
    const card = event.currentTarget
    const method = card.dataset.method
    if (!method || method === this.selectedMethod) return

    this.selectedMethod = method

    // Update card visual state
    this.methodCardTargets.forEach((c) => {
      if (c.dataset.method === method) {
        c.classList.add("checkout__method-card--selected")
      } else {
        c.classList.remove("checkout__method-card--selected")
      }
    })

    // Toggle sections
    this.stripeSectionTarget.classList.add("d-none")
    this.paypalSectionTarget.classList.add("d-none")
    this.bitcoinSectionTarget.classList.add("d-none")

    if (method === "stripe") {
      this.stripeSectionTarget.classList.remove("d-none")
      if (!this.stripeReady) this.initStripe()
    } else if (method === "paypal") {
      this.paypalSectionTarget.classList.remove("d-none")
      if (!this.paypalReady) this.initPaypal()
    } else if (method === "bitcoin") {
      this.bitcoinSectionTarget.classList.remove("d-none")
    }
  }

  // ——— Recap ———

  updateRecap() {
    // Address
    const selected = this.addressRadioTargets.find(r => r.checked)
    if (selected && this.hasRecapAddressTarget) {
      const label = selected.closest(".checkout-address-option")
        ?.querySelector(".checkout-address-option__content")
      if (label) {
        const strong = label.querySelector("strong")?.textContent || ""
        const spans = label.querySelectorAll("span.text-muted")
        const street = spans[0]?.textContent || ""
        this.recapAddressTarget.innerHTML =
          `<span class="text-muted">Fatturazione</span><span>${strong}<br><small class="text-muted">${street}</small></span>`
      }
    }

    // Payment method
    if (this.hasRecapPaymentTarget) {
      if (this.selectedMethod === "paypal") {
        this.recapPaymentTarget.innerHTML =
          `<span class="text-muted">Pagamento</span><span><i class="bi bi-paypal me-1"></i> PayPal</span>`
      } else if (this.selectedMethod === "bitcoin") {
        this.recapPaymentTarget.innerHTML =
          `<span class="text-muted">Pagamento</span><span><i class="bi bi-currency-bitcoin me-1"></i> Bitcoin</span>`
      } else {
        this.recapPaymentTarget.innerHTML =
          `<span class="text-muted">Pagamento</span><span><i class="bi bi-credit-card me-1"></i> Carta di credito</span>`
      }
    }
  }

  updateConfirmUI() {
    this.stripeSubmitSectionTarget.classList.add("d-none")
    this.paypalSubmitSectionTarget.classList.add("d-none")
    this.bitcoinSubmitSectionTarget.classList.add("d-none")

    if (this.selectedMethod === "paypal") {
      this.paypalSubmitSectionTarget.classList.remove("d-none")
      this.initPaypalConfirm()
    } else if (this.selectedMethod === "bitcoin") {
      this.bitcoinSubmitSectionTarget.classList.remove("d-none")
    } else {
      this.stripeSubmitSectionTarget.classList.remove("d-none")
    }
  }

  // ——— Stripe ———

  initStripe() {
    this.stripe = Stripe(this.publishableKeyValue)
    const elements = this.stripe.elements()
    this.cardElement = elements.create("card", {
      disableLink: true,
      style: {
        base: {
          fontSize: "16px",
          color: "#4f6d1b",
          fontFamily: '"Cormorant Garamond", serif',
          "::placeholder": { color: "#aab7c4" }
        },
        invalid: { color: "#dc3545" }
      }
    })
    this.cardElement.mount(this.cardTarget)

    this.cardElement.on("change", (event) => {
      this.errorsTarget.textContent = event.error ? event.error.message : ""
    })

    this.stripeReady = true
  }

  async submit() {
    const btn = this.submitBtnTarget
    btn.disabled = true
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Elaborazione in corso...'
    this.errorsTarget.textContent = ""

    const selected = this.addressRadioTargets.find(r => r.checked)
    const addressId = selected ? selected.value : ""

    try {
      // 1. Create PaymentIntent on backend
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const response = await fetch(this.checkoutUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          event_id: this.eventIdValue,
          address_id: addressId
        })
      })

      const data = await response.json()

      if (data.error) {
        this.errorsTarget.textContent = data.error
        this.resetButton(btn)
        return
      }

      // 2. Confirm card payment with Stripe.js
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(
        data.client_secret,
        { payment_method: { card: this.cardElement } }
      )

      if (error) {
        this.errorsTarget.textContent = error.message
        this.resetButton(btn)
      } else if (paymentIntent.status === "succeeded") {
        // 3. Redirect to confirmation
        const url = this.returnUrlValue.replace(":order_id", data.order_id)
        window.location.href = url
      }
    } catch (err) {
      this.errorsTarget.textContent = "Si è verificato un errore. Riprova."
      this.resetButton(btn)
    }
  }

  resetButton(btn) {
    btn.disabled = false
    btn.innerHTML = '<i class="bi bi-lock me-1"></i> Completa Acquisto'
  }

  // ——— PayPal ———

  initPaypal() {
    if (this.paypalReady) return
    if (typeof paypalSDK === "undefined") return

    this.paypalReady = true
    this._renderPaypalButtons(this.paypalButtonContainerTarget)
  }

  initPaypalConfirm() {
    // Render PayPal buttons in the confirmation step
    const container = this.paypalConfirmButtonContainerTarget
    container.innerHTML = ""
    this._renderPaypalButtons(container)
  }

  _renderPaypalButtons(container) {
    const self = this
    paypalSDK.Buttons({
      fundingSource: paypalSDK.FUNDING.PAYPAL,
      style: {
        layout: "vertical",
        color: "gold",
        shape: "rect",
        label: "paypal",
        height: 45
      },

      createOrder: async () => {
        const selected = self.addressRadioTargets.find(r => r.checked)
        const addressId = selected ? selected.value : ""
        const csrfToken = document.querySelector('meta[name="csrf-token"]').content

        const response = await fetch(self.paypalCreateUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({
            event_id: self.eventIdValue,
            address_id: addressId
          })
        })

        const data = await response.json()
        if (data.error) {
          self._showConfirmError(data.error)
          throw new Error(data.error)
        }

        self._lastOrderId = data.order_id
        return data.paypal_order_id
      },

      onApprove: async (data) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]').content

        const response = await fetch(self.paypalCaptureUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({
            paypal_order_id: data.orderID
          })
        })

        const result = await response.json()
        if (result.error) {
          self._showConfirmError(result.error)
          return
        }

        const url = self.returnUrlValue.replace(":order_id", result.order_id)
        window.location.href = url
      },

      onError: (err) => {
        self._showConfirmError("Si è verificato un errore con PayPal. Riprova.")
      }
    }).render(container)
  }

  // ——— Bitcoin ———

  async submitBitcoin() {
    const btn = this.bitcoinSubmitBtnTarget
    btn.disabled = true
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span> Elaborazione in corso...'

    const selected = this.addressRadioTargets.find(r => r.checked)
    const addressId = selected ? selected.value : ""

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const response = await fetch(this.bitcoinCreateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          event_id: this.eventIdValue,
          address_id: addressId
        })
      })

      const data = await response.json()

      if (data.error) {
        this._showConfirmError(data.error)
        this._resetBitcoinButton(btn)
        return
      }

      // Redirect to NOWPayments invoice page
      window.location.href = data.invoice_url
    } catch (err) {
      this._showConfirmError("Si è verificato un errore. Riprova.")
      this._resetBitcoinButton(btn)
    }
  }

  _resetBitcoinButton(btn) {
    btn.disabled = false
    btn.innerHTML = '<i class="bi bi-currency-bitcoin me-1"></i> Paga con Bitcoin'
  }

  _showConfirmError(msg) {
    if (this.hasConfirmErrorsTarget) {
      this.confirmErrorsTarget.textContent = msg
    }
  }
}
